import React, { useEffect, useState } from 'react';
import './App.css';

const DISCORD_ID = '819731408444063755';
const LANYARD_API = `https://api.lanyard.rest/v1/users/${DISCORD_ID}`;
const VISIT_TRACKER_URL = 'https://script.google.com/macros/s/AKfycbyVm4y7xyoB2_2WCP6FvqKMyrfrX_kuMLGh1eQbrbHDrvodkS_t7CWa6VACqxBuYi8HLw/exec';

const statusLabels = {
  online: 'online',
  idle: 'idle',
  dnd: 'do not disturb',
  offline: 'offline',
};

function formatCentralTime() {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago',
    timeZoneName: 'short',
  })
    .format(new Date())
    .replace(/\sC[DS]T$/, ' CT');
}

function getVisitorId() {
  const key = 'arhaan-visitor-id';
  let visitorId = localStorage.getItem(key);

  if (!visitorId) {
    visitorId = crypto.randomUUID();
    localStorage.setItem(key, visitorId);
  }

  return visitorId;
}

function trackVisit() {
  const key = 'arhaan-visit-tracked';
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, 'true');

  fetch(VISIT_TRACKER_URL, {
    method: 'POST',
    mode: 'no-cors',
    headers: {
      'Content-Type': 'text/plain;charset=utf-8',
    },
    body: JSON.stringify({
      visitorId: getVisitorId(),
      path: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      language: navigator.language,
      screen: `${window.screen.width}x${window.screen.height}`,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
  }).catch(() => {});
}

function EmailLink({ children }) {
  return (
    <a href="mailto:arhaan.jafri@gmail.com" className="smart-link">
      {children}
    </a>
  );
}

function SocialIcon({ type }) {
  if (type === 'discord') {
    return <img src="/discord-logo.png" alt="" aria-hidden="true" className="social-image social-image--discord" />;
  }

  if (type === 'linkedin') {
    return <img src="/linkedin-logo.png" alt="" aria-hidden="true" className="social-image social-image--linkedin" />;
  }

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
      <path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-8,144H40V74.19l82.59,75.71a8,8,0,0,0,10.82,0L216,74.19V192Z" />
    </svg>
  );
}

function Footer() {
  const [discordStatus, setDiscordStatus] = useState('offline');
  const [time, setTime] = useState(formatCentralTime);

  useEffect(() => {
    const updateTime = () => setTime(formatCentralTime());
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const res = await fetch(LANYARD_API);
        const data = await res.json();
        const nextStatus = data?.data?.discord_status || 'offline';
        if (isMounted) setDiscordStatus(nextStatus);
      } catch {
        if (isMounted) setDiscordStatus('offline');
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <footer className="site-footer" aria-label="Status and links">
      <div className="footer-meta">
        <span className="status-pill">
          <span className={`status-dot status-dot--${discordStatus}`} aria-hidden="true" />
          <span className="status-text">{statusLabels[discordStatus] || 'offline'}</span>
        </span>
        <span className="footer-separator" aria-hidden="true">
          ·
        </span>
        <span className="footer-time">{time}</span>
      </div>
      <nav className="social-links" aria-label="Social links">
        <a href="https://www.linkedin.com/in/arhaanjafri/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
          <SocialIcon type="linkedin" />
        </a>
        <a href="http://discord.com/users/819731408444063755" target="_blank" rel="noopener noreferrer" aria-label="Discord">
          <SocialIcon type="discord" />
        </a>
        <a href="mailto:arhaan.jafri@gmail.com" aria-label="Email">
          <SocialIcon type="email" />
        </a>
      </nav>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    document.title = 'Arhaan Jafri';
    trackVisit();
  }, []);

  return (
    <main className="site-shell">
      <section className="intro">
        <h1>Hey, I'm Arhaan.</h1>
        <div className="bio-copy">
          <p>
            I am a senior in high school from Austin, Texas. I am interested in mechanical
            engineering and hope to specialize in the humanoid robotics industry.
          </p>
          <p>
            I compete in robotics, where I qualified for the FIRST Tech Challenge (FTC) World
            Championship. I was also awarded the prestigious{' '}
            <a href="https://www.firstinspires.org/resources/library/ftc/leadership-award" target="_blank" rel="noopener noreferrer">
              FIRST Leadership Award
            </a>{' '}
            (previously known as Dean's List), an honor given to only 10 students out of 109,000
            competing students in the world.
          </p>
          <p>
            Outside of FTC, I am working as a founding engineering intern at{' '}
            <a href="https://www.protoflow.ai" target="_blank" rel="noopener noreferrer">
              ProtoFlow.ai
            </a>
            , a startup building an AI-powered desktop app that turns plain-English circuit ideas
            into editable, manufacturable schematics and PCBs.
          </p>
          <p>
            Contact me at{' '}
            <EmailLink>arhaan.jafri@gmail.com</EmailLink>.
          </p>
        </div>
      </section>
      <Footer />
    </main>
  );
}
